import graphql__8f6d63cc61d80c261776 from "./__generated__/EditBankAccountDialogCreateMutation.graphql.ts";
import graphql__49056e5a93fb4a4fb8ce from "./__generated__/EditBankAccountDialogRenameMutation.graphql.ts";
import graphql__f7b75c0918da5542c0e5 from "./__generated__/EditBankAccountDialogSetAnchorMutation.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import * as yup from "yup";
import {GraphQLTaggedNode, MutationParameters} from "relay-runtime";
import FormDialog from "./FormDialog";
import Section from "../form/Section";
import Row from "../form/Row";
import Label from "../form/Label";
import Field from "../form/Field";
import {commitMutationAsPromise} from "../../helper/commitMutationWithErrorHandling";
import {formatCentsForInput, parseAmountToCents, yupAmount} from "../../helper/amountInput";
import {useErrorContext} from "../../contexts/ErrorContext";
import type {EditBankAccountDialogCreateMutation} from "./__generated__/EditBankAccountDialogCreateMutation.graphql";
import type {EditBankAccountDialogRenameMutation} from "./__generated__/EditBankAccountDialogRenameMutation.graphql";
import type {EditBankAccountDialogSetAnchorMutation} from "./__generated__/EditBankAccountDialogSetAnchorMutation.graphql";

type Props = {
    // Without a bank account, the dialog creates one
    bankAccount?: {
        bankAccountIdentifier: string
        name: string
        anchorDate: string
        anchorBalance: number
    }
    onClose: (changed: boolean) => void
}

const createMutation = graphql__8f6d63cc61d80c261776;

const renameMutation = graphql__49056e5a93fb4a4fb8ce;

const setAnchorMutation = graphql__f7b75c0918da5542c0e5;

/**
 * Creates a bank account or changes its name and anchor. A new anchor applies at once, because
 * balances are computed from it.
 */
const EditBankAccountDialog: React.FC<Props> = ({bankAccount, onClose}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();

    const commit = <T extends MutationParameters>(mutation: GraphQLTaggedNode, input: T['variables']['input'], customErrorMessage: string) =>
        commitMutationAsPromise<T>(relayEnvironment, mutation, {input}, addError, customErrorMessage);

    const handleSubmission = async (values) => {
        const name = values.name.trim();
        const anchorDate = values.anchorDate;
        const anchorBalance = parseAmountToCents(values.anchorBalance) as number;
        try {
            if (!bankAccount) {
                await commit<EditBankAccountDialogCreateMutation>(createMutation, {name, anchorDate, anchorBalance}, intl.formatMessage({id: 'errors.failedToCreateBankAccount'}));
            } else {
                if (name !== bankAccount.name) {
                    await commit<EditBankAccountDialogRenameMutation>(renameMutation, {id: bankAccount.bankAccountIdentifier, name}, intl.formatMessage({id: 'errors.failedToRenameBankAccount'}));
                }
                if (anchorDate !== bankAccount.anchorDate || anchorBalance !== bankAccount.anchorBalance) {
                    await commit<EditBankAccountDialogSetAnchorMutation>(setAnchorMutation, {id: bankAccount.bankAccountIdentifier, anchorDate, anchorBalance}, intl.formatMessage({id: 'errors.failedToSetBankAccountAnchor'}));
                }
            }
        } catch {
            // The error was already reported, the list shows what was saved so far
        }
        onClose(true);
    };

    const validationSchema = yup.object().shape({
        name: yup.string()
            .trim()
            .max(255, intl.formatMessage({id: 'validation.field.nameTooLong'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
        anchorDate: yup.string().required(intl.formatMessage({id: 'validation.field.required'})),
        anchorBalance: yupAmount(intl),
    });

    return (
        <FormDialog handleCloseDialog={() => onClose(false)}
                    handleSubmission={handleSubmission}
                    initialValues={{
                        name: bankAccount?.name ?? '',
                        anchorDate: bankAccount?.anchorDate ?? '',
                        anchorBalance: bankAccount ? formatCentsForInput(bankAccount.anchorBalance) : '',
                    }}
                    validationSchema={validationSchema}
                    headline={intl.formatMessage({id: bankAccount ? 'dialog.EditBankAccountDialog.editHeadline' : 'dialog.EditBankAccountDialog.createHeadline'})}
                    submitLabel={intl.formatMessage({id: 'button.save'})}>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditBankAccountDialog.nameLabel'})}</Label>
                    <Field name="name" type="text" autoFocus={true} data-1p-ignore="true"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditBankAccountDialog.anchorDateLabel'})}</Label>
                    <Field name="anchorDate" type="date"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditBankAccountDialog.anchorBalanceLabel'})}</Label>
                    <Field name="anchorBalance" type="text" inputMode="decimal" placeholder="0,00" data-1p-ignore="true"/>
                </Row>
                <p className="text-sm text-gray-500">
                    {intl.formatMessage({id: 'dialog.EditBankAccountDialog.anchorDescription'})}
                </p>
            </Section>
        </FormDialog>
    );
};

export default EditBankAccountDialog;
